import { useCallback, useRef, useEffect } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $generateHtmlFromNodes } from "@lexical/html";
import {
  TextNode, LineBreakNode, $getRoot, $createParagraphNode, $createTextNode,
  PASTE_COMMAND, COMMAND_PRIORITY_HIGH, $getSelection, $isRangeSelection,
} from "lexical";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";

const theme = {
  text: { bold: "fw-bold", italic: "fst-italic" },
};

function HydratePlugin({ data, suppressNextChangeRef }) {
  const [editor] = useLexicalComposerContext();
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    const html = data?.html;
    const value = data?.value;
    if (!html && !value) return;
    hydratedRef.current = true;

    // This editor.update() below still fires OnChangePlugin's listener —
    // Lexical doesn't distinguish "loaded existing content" from "user
    // typed something" at that layer — so without this flag, mounting with
    // non-empty data calls the consumer's onChange immediately, as if the
    // user had just edited it. For TaulukkoFooter's captions that meant a
    // spurious save-with-unchanged-content on every mount, which (for
    // Peitepaksuus specifically) also re-triggers a full refresh() and a
    // second visible loading-spinner cycle right after the real one.
    suppressNextChangeRef.current = true;

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      if (html) {
        const parser = new DOMParser();
        const dom = parser.parseFromString(html, "text/html");
        const walk = (node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent ? [$createTextNode(node.textContent)] : [];
          }
          if (node.nodeType !== Node.ELEMENT_NODE) return [];
          const children = Array.from(node.childNodes).flatMap(walk);
          if (node.tagName === "P") {
            const p = $createParagraphNode();
            p.append(...children);
            return [p];
          }
          return children;
        };
        const nodes = Array.from(dom.body.childNodes).flatMap(walk);
        if (nodes.length) root.append(...nodes);
      } else {
        const p = $createParagraphNode();
        p.append($createTextNode(value));
        root.append(p);
      }
    });
  }, [editor, data?.html, data?.value, suppressNextChangeRef]);

  return null;
}

function PlainPastePlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const text = event.clipboardData?.getData('text/plain') ?? '';
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertText(text);
          }
        });
        event.preventDefault();
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);
  return null;
}

export default function MinimalLexical({ data, onChange, placeholder = "Kuvaus", plainPaste = false }) {
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);
  // Set by HydratePlugin right before it programmatically loads existing
  // content into the editor — handleChange below checks and clears it so
  // that hydration-triggered update is never mistaken for a real edit.
  const suppressNextChangeRef = useRef(false);

  const initialConfig = {
    namespace: data?.lexicalId || "minimal-lexical",
    theme,
    onError: console.error,
    nodes: [TextNode, LineBreakNode, HeadingNode, QuoteNode],
  };

  const handleChange = useCallback((editorState, editor) => {
    if (suppressNextChangeRef.current) { suppressNextChangeRef.current = false; return; }
    editorState.read(() => {
      const value = $getRoot().getTextContent();
      const html = $generateHtmlFromNodes(editor);
      onChange?.({ ...dataRef.current, value, html });
    });
  }, [onChange]);

  return (
    <LexicalComposer key={data?.lexicalId} initialConfig={initialConfig}>
      <div style={{ position: "relative" }}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className="form-control form-control-sm"
              style={{ minHeight: 58, fontSize: "0.875rem", lineHeight: 1.45 }}
            />
          }
          placeholder={
            <div style={{
              position: "absolute", top: 6, left: 10,
              color: "#adb5bd", pointerEvents: "none", fontSize: "0.875rem",
            }}>
              {placeholder}
            </div>
          }
        />
      </div>
      <HistoryPlugin />
      <OnChangePlugin onChange={handleChange} />
      <HydratePlugin data={data} suppressNextChangeRef={suppressNextChangeRef} />
      {plainPaste && <PlainPastePlugin />}
    </LexicalComposer>
  );
}
